import { AsyncForm, FormGroup, InputLabel } from '@components/async-form';
import { BtnPrimary, BtnSecondary } from '@components/buttons';
import { MeetingDuration } from '@course/components/duration-select';
import { HeadingPrimary } from '@course/components/headings';
import { rpx } from 'client/lib/rpx-client';
import { useMemo, useState } from 'preact/hooks';
import { FullCourse } from 'server/types';
import { meetingUrl } from './urls';
import { Cadence, generateSeriesOccurrences } from 'shared/scheduling/meeting-series-dates';

const DEFAULT_TZ = 'America/New_York';
const DAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

type SeriesCreatedPayload = Awaited<ReturnType<typeof rpx.meetingSeries.createMeetingSeries>>;

interface Props {
  course: Pick<FullCourse, 'id' | 'level' | 'defaultMeetingJoinUrl'>;
  onCreate: (result: SeriesCreatedPayload) => void;
}

interface FormData {
  title: string;
  calendarTitle: string;
  calendarDescription: string;
  cadence: Cadence;
  dayOfWeek: number;
  timeOfDay: string;
  timezone: string;
  durationMinutes: number;
  startDate: string;
  endDate: string;
  bridgeLink: string;
}

function defaultStartDate(): string {
  const today = new Date();
  return today.toISOString().slice(0, 10);
}

function defaultEndDate(): string {
  const d = new Date();
  d.setMonth(d.getMonth() + 3);
  return d.toISOString().slice(0, 10);
}

export function NewMeetingSeriesForm({ course, onCreate }: Props) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState<FormData>({
    title: '',
    calendarTitle: '',
    calendarDescription:
      "Your recurring coaching call.\n\nThis week's topic and prep materials:\n{{meetings_page_link}}",
    cadence: 'weekly',
    dayOfWeek: 2,
    timeOfDay: '13:00',
    timezone: DEFAULT_TZ,
    durationMinutes: 60,
    startDate: defaultStartDate(),
    endDate: defaultEndDate(),
    bridgeLink: course.defaultMeetingJoinUrl || '',
  });

  const sessionPreviewCount = useMemo(() => {
    try {
      return generateSeriesOccurrences({
        cadence: formData.cadence,
        dayOfWeek: formData.dayOfWeek,
        timeOfDay: formData.timeOfDay,
        startDate: formData.startDate,
        endDate: formData.endDate,
        timezone: formData.timezone,
      }).length;
    } catch {
      return 0;
    }
  }, [
    formData.cadence,
    formData.dayOfWeek,
    formData.timeOfDay,
    formData.startDate,
    formData.endDate,
    formData.timezone,
  ]);

  function update<K extends keyof FormData>(key: K, value: FormData[K]) {
    setFormData((s) => ({ ...s, [key]: value }));
  }

  async function submit() {
    setIsSubmitting(true);
    try {
      const result = await rpx.meetingSeries.createMeetingSeries({
        courseId: course.id,
        title: formData.title || formData.calendarTitle,
        calendarTitle: formData.calendarTitle,
        calendarDescription: formData.calendarDescription || undefined,
        cadence: formData.cadence,
        dayOfWeek: formData.dayOfWeek,
        timeOfDay: formData.timeOfDay,
        timezone: formData.timezone,
        durationMinutes: formData.durationMinutes,
        startDate: formData.startDate,
        endDate: formData.endDate,
        bridgeLink: formData.bridgeLink || undefined,
        sendCalendarInvites: true,
      });
      onCreate(result);
    } finally {
      setIsSubmitting(false);
    }
  }

  const sessionPreviewLabel =
    sessionPreviewCount === 0
      ? 'No sessions match this schedule. Check the start date and day of week.'
      : `Will create ${sessionPreviewCount} session${sessionPreviewCount === 1 ? '' : 's'} (${formData.startDate} → ${formData.endDate}).`;

  return (
    <AsyncForm class="flex flex-col py-8 w-full max-w-5xl mx-auto" onSubmit={submit}>
      <HeadingPrimary title="New recurring series" />

      <FormGroup prop="title" class="mb-6">
        <InputLabel>Series title (internal label)</InputLabel>
        <input
          type="text"
          placeholder="AIP Cohort Fall 2026"
          name="title"
          class="inline-ruz-input w-full"
          value={formData.title}
          onChange={(e: any) => update('title', e.target.value)}
        />
        <span class="text-xs text-gray-500 mt-1">
          For your own reference. Not shown to students.
        </span>
      </FormGroup>

      <FormGroup prop="calendarTitle" class="mb-6">
        <InputLabel>Calendar title (what students see)</InputLabel>
        <input
          type="text"
          placeholder="AIP Coaching Call"
          name="calendarTitle"
          class="inline-ruz-input w-full"
          value={formData.calendarTitle}
          onChange={(e: any) => update('calendarTitle', e.target.value)}
        />
        <span class="text-xs text-gray-500 mt-1">
          Appears as the title of every recurring entry in students' calendars.
        </span>
      </FormGroup>

      <div class="mb-6 grid grid-cols-2 gap-4">
        <FormGroup prop="cadence">
          <InputLabel>Cadence</InputLabel>
          <select
            class="inline-ruz-input w-full"
            value={formData.cadence}
            onChange={(e: any) => update('cadence', e.target.value as Cadence)}
          >
            <option value="weekly">Weekly</option>
            <option value="biweekly">Every other week</option>
          </select>
        </FormGroup>

        <FormGroup prop="dayOfWeek">
          <InputLabel>Day of week</InputLabel>
          <select
            class="inline-ruz-input w-full"
            value={String(formData.dayOfWeek)}
            onChange={(e: any) => update('dayOfWeek', Number(e.target.value))}
          >
            {DAY_LABELS.map((label, i) => (
              <option key={i} value={String(i)}>
                {label}
              </option>
            ))}
          </select>
        </FormGroup>
      </div>

      <div class="mb-6 grid grid-cols-2 gap-4">
        <FormGroup prop="timeOfDay">
          <InputLabel>Time (24h)</InputLabel>
          <input
            type="time"
            class="inline-ruz-input w-full"
            value={formData.timeOfDay}
            onChange={(e: any) => update('timeOfDay', e.target.value)}
          />
        </FormGroup>

        <FormGroup prop="timezone">
          <InputLabel>Timezone (IANA)</InputLabel>
          <input
            type="text"
            class="inline-ruz-input w-full"
            value={formData.timezone}
            onChange={(e: any) => update('timezone', e.target.value)}
            placeholder="America/New_York"
          />
        </FormGroup>
      </div>

      <FormGroup prop="durationMinutes" class="mb-6">
        <InputLabel>Duration</InputLabel>
        <div class="mt-2">
          <MeetingDuration
            name="durationMinutes"
            value={formData.durationMinutes}
            onNewDuration={(d) => update('durationMinutes', d)}
          />
        </div>
      </FormGroup>

      <div class="mb-6 grid grid-cols-2 gap-4">
        <FormGroup prop="startDate">
          <InputLabel>Starts</InputLabel>
          <input
            type="date"
            class="inline-ruz-input w-full"
            value={formData.startDate}
            onChange={(e: any) => update('startDate', e.target.value)}
          />
        </FormGroup>
        <FormGroup prop="endDate">
          <InputLabel>Ends</InputLabel>
          <input
            type="date"
            class="inline-ruz-input w-full"
            value={formData.endDate}
            onChange={(e: any) => update('endDate', e.target.value)}
          />
        </FormGroup>
      </div>

      <FormGroup prop="bridgeLink" class="mb-6">
        <InputLabel>Bridge link (shared Zoom URL)</InputLabel>
        <input
          type="url"
          placeholder="https://zoom.us/j/..."
          class="inline-ruz-input w-full"
          value={formData.bridgeLink}
          onChange={(e: any) => update('bridgeLink', e.target.value)}
        />
        <span class="text-xs text-gray-500 mt-1">
          Same link, every session. Leave blank to save as a draft (no calendar invites sent until
          you add a link).
        </span>
      </FormGroup>

      <div class="mb-6 border rounded-lg p-4 bg-gray-50 dark:bg-gray-800">
        <InputLabel>Calendar entry shown to students</InputLabel>
        <textarea
          class="inline-ruz-input w-full mt-2"
          rows={4}
          value={formData.calendarDescription}
          onChange={(e: any) => update('calendarDescription', e.target.value)}
        />
        <span class="text-xs text-gray-500 mt-1 block">
          Use <code class="bg-white dark:bg-gray-900 px-1 rounded">{'{{meetings_page_link}}'}</code>{' '}
          to insert a link to this course's meetings page.
        </span>
      </div>

      <div class="mb-6 p-3 rounded bg-blue-50 dark:bg-blue-900/30 text-sm">
        {sessionPreviewLabel}
      </div>

      <footer class="flex mt-2 justify-end">
        <BtnPrimary
          class="px-8 py-3"
          isLoading={isSubmitting}
          disabled={sessionPreviewCount === 0 || !formData.calendarTitle.trim()}
        >
          Create series
        </BtnPrimary>
        <BtnSecondary
          type="button"
          class="ml-4 px-8 py-3"
          href={meetingUrl({ courseId: course.id })}
        >
          Cancel
        </BtnSecondary>
      </footer>
    </AsyncForm>
  );
}
